import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { setUser } from '../actions/userActions';
import { setNetworks } from '../actions/networksActions';
import { trackEvent } from '../helpers/analytics';
import { useSignInMutation } from '../services/user';
import { setupUserSession } from '../services/auth';
import { getItem, storeItem, removeItem } from '../helpers/store';
import { useSocialAuth } from './useSocialAuth';

export const MFA_CHALLENGE_TOKEN_KEY = 'mfaChallengeToken';
// Companions persisted by the verify step so a query-only email deep link (and a reload)
// keeps the post-verify destination and invite flag, not just the token.
export const MFA_REDIRECT_KEY = 'mfaChallengeRedirect';
export const MFA_INVITE_KEY = 'mfaChallengeInvite';
export const MFA_VERIFY_PATH = '/mfa/verify';
export const MFA_SETUP_TOKEN_KEY = 'mfaSetupToken';
export const MFA_SETUP_FROM_KEY = 'mfaSetupFrom';
export const MFA_SETUP_PATH = '/mfa/setup';

const isSafeFromLocation = (from) =>
  typeof from?.pathname === 'string' && from.pathname.startsWith('/') && !from.pathname.startsWith('//');

export const storeMfaSetupFrom = (from) => {
  if (!isSafeFromLocation(from)) {
    return;
  }

  storeItem(MFA_SETUP_FROM_KEY, JSON.stringify({ pathname: from.pathname, search: from.search ?? '' }));
};

export const readMfaSetupFrom = () => {
  const stored = getItem(MFA_SETUP_FROM_KEY);
  if (!stored) {
    return null;
  }

  try {
    const from = JSON.parse(stored);
    return isSafeFromLocation(from) ? { pathname: from.pathname, search: from.search ?? '' } : null;
  } catch (error) {
    return null;
  }
};

// Wipe every challenge-scoped item. Called on sign-out and before starting a new in-app
// challenge, so a stale destination/flag from an abandoned email deep link can't bleed into
// a later login (e.g. routing it to the wrong URL).
export const clearMfaChallengeStorage = () => {
  removeItem(MFA_CHALLENGE_TOKEN_KEY);
  removeItem(MFA_REDIRECT_KEY);
  removeItem(MFA_INVITE_KEY);
};

export const useSignIn = ({ isInvite = false } = { isInvite: false }) => {
  const [userSignIn, { isLoading }] = useSignInMutation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const handleSocialAuth = useSocialAuth(isInvite);

  const signIn = (values) =>
    userSignIn(values)
      .unwrap()
      .then((response) => {
        // Two-step login: backend asks for a TOTP/backup code before issuing a session.
        if (response?.mfaRequired) {
          // Clear any leftover challenge state (e.g. an abandoned deep link) before seeding
          // this fresh challenge, so the verify step can't pick up a stale destination/flag.
          clearMfaChallengeStorage();
          storeItem(MFA_CHALLENGE_TOKEN_KEY, response.challengeToken);
          // Carry the protected-route `from` through the MFA hop so the verify step can
          // restore the originally requested URL instead of falling back to home.
          // `isInvite` rides along so the verify step can re-run the invite email-match
          // check (no `user` is available until after verify succeeds).
          history.push(MFA_VERIFY_PATH, {
            challengeToken: response.challengeToken,
            from: location.state?.from,
            isInvite,
          });

          return { mfaRequired: true };
        }

        // Network requires MFA but this user hasn't enrolled yet: force enrollment before a session.
        if (response?.mfaSetupRequired) {
          storeItem(MFA_SETUP_TOKEN_KEY, response.setupToken);
          // Carry `from` through forced enrollment so the post-enroll redirect can
          // restore the originally requested URL. Persist it as well so a reload
          // mid-setup does not drop the destination when router state is lost.
          storeMfaSetupFrom(location.state?.from);
          history.push(MFA_SETUP_PATH, { setupToken: response.setupToken, from: location.state?.from });

          return { mfaSetupRequired: true };
        }

        const { user } = response;

        handleSocialAuth(user);

        return setupUserSession(user);
      })
      .then((result) => {
        if (result?.mfaRequired || result?.mfaSetupRequired) {
          return result;
        }

        const user = result;
        dispatch(setUser(user));
        dispatch(setNetworks(user));

        return user;
      })
      .catch((error) => {
        if (error?.status === 401) {
          trackEvent('log_in_invalid');
        }

        throw error;
      });

  return { signIn, isLoading };
};
